import Sidebar from "./components/sideBar/Sidebar";
import styles from "./NewManager.module.css";
import BottomBar from "./components/sideMain/bottomBar/BottomBar";
import { useContext, useState, useRef, useEffect } from "react";
import { SideCardType } from "../../util/QuestionStatus";
import QuestionList from "./components/sideMain/questionList/QuestionList";
import { AuthContext } from "../../context/AuthContext";
import SelectAllBar from "./components/sideMain/topBar/SelectAllBar";
import TopBar from "./components/sideMain/topBar/TopBar";
import MaxList from "./components/sideMain/contextList/ContextList";
import BrainList from "./components/sideMain/brainList/BrainList";
import CustomButton, { ButtonType } from "../../components/button/CustomButton";
import MyChatBot from "../chatbot/MyChatBot";
import SearchBar from "../../components/searchBar/SearchBar";
import TabBar from "../../components/tabBar/TabBar";
import ChatbotButton from "../chatbot/ChatbotButton";
import ImagesList from "./components/sideMain/imagesList/ImagesList";
import AddNewImageModal from "./components/sideMain/imagesList/components/modals/AddNewImageModal";
import { KnowledgeType } from "../../util/KnowledgeType";
import AddKnowledgeModal from "./components/sideMain/brainList/modal/AddKnowledgeModal";
import ChatHistory from "./components/sideMain/chat/ChatHistory";
import ChatFilterModal from "./components/sideMain/brainList/modal/ChatFilterModal";
import { useAppDispatch, useAppSelector } from "../../store/common/hooks";
import { UploadImageFormData } from "../../api/ImageApiCall";
import { fetchChatHistory } from "../../store/chat.slice";
import { fetchImages } from "../../store/images.slice";
import { fetchContext } from "../../store/context.slice";
import { fetchBrainList } from "../../store/brain.slice";
import { fetchConversations } from "../../store/conversations.slice";
import { fetchIntents } from "../../store/intent.slice";
import { fetchSopKnowledge } from "../../store/sop.slice";
import { fetchCategories } from "../../store/categories.slice";

const NewManager = () => {
  const { isSignedIn } = useContext(AuthContext);
  const knowledgeType = useAppSelector((s) => s.brain.knowledgeType);
  const bodyRef = useRef<HTMLDivElement>(null);

  const dispatch = useAppDispatch();

  const [openChatBot, setOpenChatBot] = useState(false);
  const [openAddNewImageModal, setOpenAddNewImageModal] = useState(false);

  const [openAddNewKnowledgeModal, setOpenAddNewKnowledgeModal] =
    useState(false);

  const [openConversationFilterModal, setOpenConversationFilterModal] =
    useState(false);

  if (!isSignedIn) {
    return <div>Loading Conversations...</div>;
  }

  const statusClicked = useAppSelector((s) => s.ui.statusClicked);
  const currentPage = useAppSelector((s) => s.pagination.currentPage);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;

    requestAnimationFrame(() => {
      el.scrollTop = 0;
      el.scrollTo?.({ top: 0, behavior: "smooth" });
    });
  }, [currentPage]);

  useEffect(() => {
    if (!isSignedIn) return;
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    if (!isSignedIn) return;

    switch (statusClicked) {
      case SideCardType.NeedApproval:
      case SideCardType.Rejected:
        dispatch(fetchConversations({}));
        break;

      case SideCardType.Context:
        dispatch(fetchContext({}));
        break;

      case SideCardType.Brain:
        if (knowledgeType === KnowledgeType.SOP) {
          dispatch(fetchSopKnowledge({}));
        } else if (knowledgeType === KnowledgeType.INTENT) {
          dispatch(fetchIntents({}));
        } else {
          dispatch(fetchBrainList({}));
        }
        break;

      case SideCardType.Images:
        dispatch(fetchImages({}));
        break;

      case SideCardType.Chat:
        dispatch(fetchChatHistory({}));
        break;
    }
  }, [dispatch, isSignedIn, statusClicked, knowledgeType]);

  const handleSyncAll = () => {
    dispatch(fetchContext());
  };

  const handleOpenChatBot = () => {
    setOpenChatBot((prev) => !prev);
  };

  const handleAddNewImage = () => {
    setOpenAddNewImageModal((prev) => !prev);
  };

  const handleAddNewKnowledge = () => {
    setOpenAddNewKnowledgeModal((prev) => !prev);
  };

  const handleFilterChat = () => {
    setOpenConversationFilterModal((prev) => !prev);
  };

  const handleClearChatHistory = () => {
    dispatch(fetchChatHistory());
  };

  const renderTopSection = () => (
    <div>
      <TopBar />
      {statusClicked === SideCardType.Rejected && <SelectAllBar />}
      {statusClicked === SideCardType.Brain && (
        <>
          <TabBar />
          <SearchBar />
          {(knowledgeType === KnowledgeType.SOP ||
            knowledgeType === KnowledgeType.INTENT) && (
            <div className={styles["sync-all"]}>
              <CustomButton
                text="Add Knowledge"
                type={ButtonType.Edit}
                onClick={handleAddNewKnowledge}
              />
            </div>
          )}
        </>
      )}
      {statusClicked === SideCardType.Context && (
        <div className={styles["sync-all"]}>
          <CustomButton
            text="Update Context"
            type={ButtonType.Normal}
            onClick={handleSyncAll}
          />
        </div>
      )}
      {statusClicked === SideCardType.Images && (
        <div className={styles["sync-all"]}>
          <CustomButton
            text="Add New Image"
            type={ButtonType.Edit}
            onClick={handleAddNewImage}
          />
        </div>
      )}
      {statusClicked === SideCardType.Chat && (
        <div className={styles["sync-all"]}>
          <CustomButton
            text="Filter Chat"
            type={ButtonType.Normal}
            onClick={handleFilterChat}
          />
          <CustomButton
            text="Clear"
            type={ButtonType.Cancel}
            onClick={handleClearChatHistory}
          />
        </div>
      )}
    </div>
  );

  const handleOnSendClicked = async (
    file: File | null,
    name: string,
    description: string,
    category: number
  ) => {
    setOpenAddNewImageModal(false);
    if (file) {
      console.log(name, description, category);
      await UploadImageFormData(name, description, category, file);
      dispatch(fetchImages());
    }
  };

  const renderBodySection = () => {
    if (statusClicked === SideCardType.Context) return <MaxList />;
    if (statusClicked === SideCardType.Brain) return <BrainList />;
    if (statusClicked === SideCardType.Images) return <ImagesList />;
    if (statusClicked === SideCardType.Chat) return <ChatHistory />;

    return <QuestionList />;
  };

  return (
    <div className={styles["main-container"]}>
      <Sidebar />
      <main>
        {renderTopSection()}
        <div id="body-view" className={styles["body-view"]} ref={bodyRef}>
          {renderBodySection()}
        </div>
        <BottomBar />
      </main>
      <AddNewImageModal
        isOpen={openAddNewImageModal}
        onClose={() => {
          setOpenAddNewImageModal(false);
        }}
        onSendClicked={handleOnSendClicked}
      />

      <AddKnowledgeModal
        isOpen={openAddNewKnowledgeModal}
        onClose={() => {
          setOpenAddNewKnowledgeModal(false);
        }}
      />

      <ChatFilterModal
        isOpen={openConversationFilterModal}
        onClose={() => {
          setOpenConversationFilterModal(false);
        }}
      />

      {openChatBot && (
        <div
          style={{
            backgroundColor: "#00000045",
            position: "absolute",
            width: "100%",
            height: "100%",
          }}
          onClick={handleOpenChatBot}
        >
          <div
            style={{ position: "absolute", bottom: "12.5rem", right: "5rem" }}
            onClick={(e) => e.stopPropagation()}
          >
            <MyChatBot
              chatWindowStyle={{
                height: "70dvh",
                width: "100%",
              }}
            />
          </div>
        </div>
      )}
      <ChatbotButton />
    </div>
  );
};

export default NewManager;
